import { Injectable, inject } from '@angular/core';
import { firstValueFrom, take } from 'rxjs';
import { GridTransaction } from 'src/app/shared/components/grid/grid-transaction';
import { EfateStudyOptions } from 'src/app/shared/models/echo/efate-study-options';
import { EfDataSetRow } from 'src/app/shared/models/ef-data.model';
import { EfateSetupApiService } from 'src/app/shared/services/echo/efate-setup.api.service';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';

@Injectable({
  providedIn: 'root',
})
export class EfDataStudyOptionsLogicService {
  private readonly efateSetupApiService = inject(EfateSetupApiService);

  public async getByDatasetPk(datasetPk: number): Promise<EfateStudyOptions | undefined> {
    if (!datasetPk || datasetPk <= 0) {
      return undefined;
    }

    try {
      const studyOptions = await firstValueFrom(this.efateSetupApiService.getEfateStudyOptionsByDatasetPk(datasetPk).pipe(take(1)));
      return this.isStoredRecord(studyOptions) ? studyOptions : undefined;
    } catch (error) {
      console.warn('Error loading CAKE study options', error);
      return undefined;
    }
  }

  public isStoredRecord(studyOptions?: EfateStudyOptions | null): boolean {
    return (studyOptions?.efateStudyOptionsPk ?? 0) > 0 && (studyOptions?.datasetPk ?? 0) > 0;
  }

  public createDefault(row: EfDataSetRow, efateSetupPk?: number): EfateStudyOptions {
    return {
      efateStudyOptionsPk: 0,
      datasetPk: row?.efateCakeDataSetPk ?? 0,
      efateSetupPk: efateSetupPk ?? row?.efateSetupPk ?? 0,
      name: EFateConstants.DEFAULT_STUDY_OPTIONS_NAME,
      description: EFateConstants.DEFAULT_STUDY_OPTIONS_DESCRIPTION,
      timeEnteredIn: EFateConstants.DEFAULT_TIME_UNIT,
      concentrationEnteredIn: EFateConstants.DEFAULT_CONCENTRATION_UNIT,
      comment: row?.studyType ?? null,
    };
  }

  public parseCreatedDate(createdDate?: string | null): Date | undefined {
    if (createdDate === null || createdDate === undefined || `${createdDate}`.trim() === '') {
      return undefined;
    }

    const parsed = new Date(createdDate);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }

  public formatCreatedDate(createdDate?: Date | null): string | undefined {
    if (!(createdDate instanceof Date) || isNaN(createdDate.getTime())) {
      return undefined;
    }

    const pad = (value: number) => `${value}`.padStart(2, '0');
    return `${createdDate.getFullYear()}-${pad(createdDate.getMonth() + 1)}-${pad(createdDate.getDate())}T00:00:00`;
  }

  public isNameValid(studyOptions?: EfateStudyOptions): boolean {
    const name = (studyOptions?.name ?? '').trim();
    return name !== '' && name.length <= EFateConstants.MAX_STUDY_OPTIONS_NAME_LENGTH;
  }

  public isDescriptionValid(studyOptions?: EfateStudyOptions): boolean {
    return (studyOptions?.description ?? '').length <= EFateConstants.MAX_STUDY_OPTIONS_DESCRIPTION_LENGTH;
  }

  public isTimeUnitValid(timeEnteredIn?: string | null): boolean {
    return EFateConstants.TIME_UNIT_OPTIONS.some((option) => option.value === timeEnteredIn);
  }

  public isConcentrationUnitValid(concentrationEnteredIn?: string | null): boolean {
    return EFateConstants.CONCENTRATION_UNIT_OPTIONS.some((option) => option.value === concentrationEnteredIn);
  }

  public isLinkedToDataSet(studyOptions?: EfateStudyOptions): boolean {
    return (studyOptions?.datasetPk ?? 0) > 0 && (studyOptions?.efateSetupPk ?? 0) > 0;
  }

  public isValid(studyOptions?: EfateStudyOptions): boolean {
    if (!studyOptions) {
      return false;
    }

    return this.isNameValid(studyOptions)
      && this.isDescriptionValid(studyOptions)
      && this.isTimeUnitValid(studyOptions.timeEnteredIn)
      && this.isConcentrationUnitValid(studyOptions.concentrationEnteredIn)
      && this.isLinkedToDataSet(studyOptions);
  }

  public async save(studyOptions: EfateStudyOptions): Promise<EfateStudyOptions | undefined> {
    const isNew = (studyOptions.efateStudyOptionsPk ?? 0) <= 0;
    const transactionType = isNew ? GridTransaction.insert : GridTransaction.update;

    if (!await this.sendTransaction(studyOptions, transactionType)) {
      return undefined;
    }

    return await this.getByDatasetPk(studyOptions.datasetPk);
  }

  private async sendTransaction(studyOptions: EfateStudyOptions, transactionType: number): Promise<boolean> {
    const row: EfateStudyOptions = { ...studyOptions };
    if (row.createdDate === undefined || row.createdDate === null) {
      delete row.createdDate;
    }

    const transactions = [{ transactionType, row }];

    try {
      const response = await firstValueFrom(this.efateSetupApiService.saveEfateStudyOptions(transactions).pipe(take(1)));
      return this.isSaveAccepted(response);
    } catch (error) {
      console.warn('Error saving CAKE study options', error);
      return false;
    }
  }

  private isSaveAccepted(response: any): boolean {
    return response === true;
  }
}
